<script lang="ts">
  import Button from '$lib/components/reusable/Button.svelte';
  import HorizontalDotMenuIcon from '$icons/menus/HorizontalDotMenuIcon.svelte';
  import { type ProjectComment } from '$shared/lib';
  import EditPencilIcon from '$icons/edit/EditPencilIcon.svelte';
  import DeleteIcon from '$icons/DeleteIcon.svelte';
  import type { User } from 'src/routes/(app)/+layout.server';
  import { onMount } from 'svelte';
  import { client } from '$lib/api/Client';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { projectCommentsQueryKey } from '$lib/pages/Projects/HackathonProjectComments.svelte';
  import Modal from '$lib/components/reusable/Modal.svelte';
  import { toastCustom } from '$utils/toasts';
  import { ClientResponseError } from '$lib/api/ClientResponseError';
  import type { ListProjectCommentsResponse } from '$shared/api';

  export let currentUser: User | null;
  export let comment: ProjectComment;
  export let showEditComment = false;

  let showDropdown = false;
  let dropdownElement: HTMLDivElement;
  let confirmDeleteModalOpen = false;

  const queryClient = useQueryClient();
  const deleteCommentMutation = createMutation({
    mutationFn: async () => {
      return client.projects.deleteComment({
        commentId: comment.id,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: projectCommentsQueryKey(comment.projectId),
      });

      const previousData = queryClient.getQueryData<{
        pages: ListProjectCommentsResponse[];
      }>(projectCommentsQueryKey(comment.projectId));

      if (previousData) {
        const updateCommentInTree = (comments: ProjectComment[]): ProjectComment[] => {
          return comments.map((c) => {
            if (c.id === comment.id) {
              return {
                ...c,
                isDeleted: true,
                body: {
                  type: 'doc',
                  content: [
                    {
                      type: 'paragraph',
                      content: [
                        {
                          type: 'text',
                          text: '[This comment has been deleted by the user.]',
                        },
                      ],
                    },
                  ],
                },
              };
            }
            if (c.replies?.length) {
              return { ...c, replies: updateCommentInTree(c.replies) };
            }
            return c;
          });
        };

        queryClient.setQueryData(projectCommentsQueryKey(comment.projectId), {
          ...previousData,
          pages: previousData.pages.map((page) => ({
            ...page,
            comments: updateCommentInTree(page.comments),
          })),
        });
      }

      return { previousData };
    },
    onError: (error, _, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(projectCommentsQueryKey(comment.projectId), context.previousData);
      }

      if (error instanceof ClientResponseError) {
        toastCustom({
          title: 'Error',
          description: error.message,
          negative: true,
          position: 'bottom-center',
        });
      } else {
        toastCustom({
          title: 'Error',
          description: 'An unknown error occurred while deleting the comment.',
          negative: true,
          position: 'bottom-center',
        });
      }
    },
  });

  $: dropdownActions = [
    {
      label: 'Edit comment',
      icon: EditPencilIcon,
      onClick: () => {
        showEditComment = true;
        showDropdown = false;
      },
      show: comment.user.id === currentUser?.userId,
    },
    {
      label: 'Delete comment',
      icon: DeleteIcon,
      onClick: () => {
        confirmDeleteModalOpen = true;
        showDropdown = false;
      },
      show: comment.user.id === currentUser?.userId,
    },
  ];

  function handleClickOutside(event: MouseEvent) {
    if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
      showDropdown = false;
    }
  }

  onMount(() => {
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  $: if (showDropdown) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }

  $: hasAvailableActions = dropdownActions.some((action) => action.show);

  async function handleDeleteComment() {
    await $deleteCommentMutation.mutateAsync();
    confirmDeleteModalOpen = false;
  }
</script>

{#if hasAvailableActions}
  <div class="relative z-10" bind:this={dropdownElement}>
    <Button quinary icon on:click={() => (showDropdown = !showDropdown)}>
      <HorizontalDotMenuIcon />
    </Button>
    {#if showDropdown}
      <div class="bg-gray-dark-4 text-gray-dark-12 absolute right-0 w-56 rounded py-1">
        {#each dropdownActions as action}
          {#if action.show}
            <button
              on:click={action.onClick}
              class="px-lg text-gray-dark-12 focus-within:ring-mint-dark-4 hover:bg-gray-dark-2 focus:border-mint-dark-8 flex w-full items-center gap-x-2 border border-transparent py-2 text-sm leading-5 ring-[3px] ring-transparent"
            >
              <svelte:component this={action.icon} />
              {action.label}
            </button>
          {/if}
        {/each}
      </div>
    {/if}
  </div>
{/if}

<!-- Delete comment modal -->
<Modal
  bind:modalOpen={confirmDeleteModalOpen}
  title="Delete Comment"
  description="Are you sure you want to delete this comment? This action cannot be undone."
>
  <p class="text-gray-dark-10 text-sm">
    Are you sure you want to delete this comment? This action cannot be undone.
  </p>
  <div class="mt-4 flex justify-end gap-3">
    <Button
      secondary
      on:click={() => (confirmDeleteModalOpen = false)}
      disabled={$deleteCommentMutation.isPending}
    >
      Cancel
    </Button>
    <Button
      danger
      on:click={handleDeleteComment}
      loading={$deleteCommentMutation.isPending}
      disabled={$deleteCommentMutation.isPending}
    >
      Delete
    </Button>
  </div>
</Modal>
